<template>
  <div class="legend">
    <div class="legend-head">
      <span class="legend-title">{{ series.length }} series</span>
      <div class="legend-sort">
        <label class="legend-sort-label" :for="sortId">Sort</label>
        <LgSelect
          :id="sortId"
          class="legend-sort-select"
          :model-value="sortBy"
          :options="sortOptions"
          aria-label="Sort series"
          @update:model-value="sortBy = $event as SortKey"
        />
      </div>
    </div>

    <ul class="legend-list" @mouseleave="$emit('hover', null)">
      <li
        v-for="row in visibleRows"
        :key="row.name"
        class="legend-row"
        :class="{ hidden: isHidden(row.name), dim: isDimmed(row.name) }"
        @mouseenter="$emit('hover', row.name)"
      >
        <button
          type="button"
          class="legend-swatch-btn"
          :aria-label="`Change color for ${displayName(row.name)}`"
          title="Series color"
          @click.stop
        >
          <span class="legend-swatch" :style="{ background: row.color }" />
          <input
            class="legend-color-input"
            type="color"
            :value="toHex(row.color)"
            :aria-label="`Color for ${displayName(row.name)}`"
            @input="onColor(row.name, $event)"
          />
        </button>

        <button
          type="button"
          class="legend-name-btn"
          :title="`Isolate ${displayName(row.name)} (click) — shift-click to add`"
          @click="onIsolate(row.name, $event)"
        >
          <span class="legend-name">{{ displayName(row.name) }}</span>
        </button>

        <span class="legend-stats">
          <span class="legend-current">{{ fmt(row.current) }}</span>
          <span class="legend-range">{{ fmt(row.min) }} – {{ fmt(row.max) }}</span>
        </span>

        <button
          type="button"
          class="legend-icon-btn"
          :aria-label="`Rename ${displayName(row.name)}`"
          title="Rename series"
          @click.stop="startAlias(row.name)"
        >
          <Pencil :size="12" aria-hidden="true" />
        </button>
        <button
          type="button"
          class="legend-icon-btn"
          :aria-pressed="isHidden(row.name)"
          :aria-label="
            isHidden(row.name) ? `Show ${displayName(row.name)}` : `Hide ${displayName(row.name)}`
          "
          :title="isHidden(row.name) ? 'Show' : 'Hide'"
          @click.stop="$emit('toggle-hidden', row.name)"
        >
          <EyeOff v-if="isHidden(row.name)" :size="12" aria-hidden="true" />
          <Eye v-else :size="12" aria-hidden="true" />
        </button>
      </li>

      <li v-if="hiddenTail > 0" class="legend-more">
        <button type="button" class="legend-more-btn" @click="expanded = true">
          + {{ hiddenTail }} more
        </button>
      </li>
    </ul>

    <LgModal :open="aliasName !== null" title="Rename series" @close="cancelAlias">
      <div class="alias-body">
        <p class="alias-orig">{{ aliasName }}</p>
        <LgInput
          v-model="aliasDraft"
          placeholder="Series alias"
          aria-label="Series alias"
          @keydown.enter.prevent="commitAlias"
        />
      </div>
      <template #footer>
        <LgButton size="sm" variant="ghost" @click="cancelAlias">Cancel</LgButton>
        <LgButton size="sm" variant="primary" @click="commitAlias">Save</LgButton>
      </template>
    </LgModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { Eye, EyeOff, Pencil } from 'lucide-vue-next';
import { formatMetric, type MetricUnit } from '../../lib/format-metric.js';
import LgSelect from '../../ui/LgSelect.vue';
import LgModal from '../../ui/LgModal.vue';
import LgInput from '../../ui/LgInput.vue';
import LgButton from '../../ui/LgButton.vue';

export interface LegendRow {
  name: string;
  color: string;
  current: number | null;
  min: number | null;
  max: number | null;
  /** Spread (max-min) — drives the "noisiest first" sort. */
  variance: number;
}

type SortKey = 'value' | 'variance' | 'name';

const props = withDefaults(
  defineProps<{
    series: LegendRow[];
    unit?: MetricUnit;
    aliases?: Record<string, string>;
    colors?: Record<string, string>;
    /** Series names currently hidden. */
    hidden?: string[];
    /** Cap before collapsing the tail into "+N more". */
    cap?: number;
  }>(),
  { unit: 'none', aliases: () => ({}), colors: () => ({}), hidden: () => [], cap: 12 },
);

const emit = defineEmits<{
  isolate: [names: string[]];
  'toggle-hidden': [name: string];
  hover: [name: string | null];
  'set-alias': [name: string, alias: string];
  'set-color': [name: string, color: string];
}>();

const sortId = `legend-sort-${Math.floor(Math.random() * 1e6)}`;
const sortOptions = [
  { value: 'value', label: 'Current value' },
  { value: 'variance', label: 'Noisiest first' },
  { value: 'name', label: 'Name' },
];
const sortBy = ref<SortKey>('value');
const expanded = ref(false);

const sortedRows = computed<LegendRow[]>(() => {
  const rows = [...props.series];
  if (sortBy.value === 'name') {
    rows.sort((a, b) => displayName(a.name).localeCompare(displayName(b.name)));
  } else if (sortBy.value === 'variance') {
    rows.sort((a, b) => b.variance - a.variance);
  } else {
    rows.sort((a, b) => (b.current ?? -Infinity) - (a.current ?? -Infinity));
  }
  return rows;
});

const visibleRows = computed<LegendRow[]>(() =>
  expanded.value ? sortedRows.value : sortedRows.value.slice(0, props.cap),
);
const hiddenTail = computed(() =>
  expanded.value ? 0 : Math.max(0, sortedRows.value.length - props.cap),
);

// --- Isolate / hover ------------------------------------------------------
/** Names the user has isolated (only these render). Empty = show all. */
const isolatedNames = ref<Set<string>>(new Set());

function onIsolate(name: string, ev: MouseEvent) {
  const set = new Set(isolatedNames.value);
  if (ev.shiftKey) {
    // Shift-click adds/removes from the isolated set.
    set.has(name) ? set.delete(name) : set.add(name);
  } else if (set.size === 1 && set.has(name)) {
    set.clear(); // clicking the sole isolated series again clears isolation
  } else {
    set.clear();
    set.add(name);
  }
  isolatedNames.value = set;
  emit('isolate', [...set]);
}

function isDimmed(name: string): boolean {
  return isolatedNames.value.size > 0 && !isolatedNames.value.has(name);
}
function isHidden(name: string): boolean {
  return props.hidden.includes(name);
}

// --- Aliases / colors -----------------------------------------------------
function displayName(name: string): string {
  return props.aliases[name] || name;
}
function fmt(v: number | null): string {
  return v == null ? '—' : formatMetric(v, props.unit);
}

const aliasName = ref<string | null>(null);
const aliasDraft = ref('');
function startAlias(name: string) {
  aliasName.value = name;
  aliasDraft.value = props.aliases[name] || '';
}
function commitAlias() {
  if (aliasName.value !== null) emit('set-alias', aliasName.value, aliasDraft.value.trim());
  aliasName.value = null;
}
function cancelAlias() {
  aliasName.value = null;
}

function onColor(name: string, ev: Event) {
  const value = (ev.target as HTMLInputElement).value;
  emit('set-color', name, value);
}
/** `<input type=color>` needs a 6-digit hex; pass token/rgb colors through as
 * a best-effort (the native picker just falls back to black if it can't parse). */
function toHex(color: string): string {
  return /^#[0-9a-fA-F]{6}$/.test(color) ? color : '#000000';
}
</script>

<style scoped>
.legend {
  display: flex;
  flex-direction: column;
  min-height: 0;
  border: 1px solid var(--lg-border-faint);
  border-radius: var(--lg-radius-md);
  background: var(--lg-bg-raised);
}
.legend-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--lg-space-2);
  padding: var(--lg-space-2) var(--lg-space-3);
  border-bottom: 1px solid var(--lg-border-faint);
}
.legend-title {
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.legend-sort {
  display: inline-flex;
  align-items: center;
  gap: var(--lg-space-2);
}
.legend-sort-label {
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
}
.legend-sort-select {
  font-size: var(--lg-fs-sm);
}
.legend-list {
  margin: 0;
  padding: var(--lg-space-1);
  list-style: none;
  overflow-y: auto;
  min-height: 0;
}
.legend-row {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  padding: var(--lg-space-1) var(--lg-space-2);
  border-radius: var(--lg-radius-sm);
}
.legend-row:hover {
  background: var(--lg-bg-hover);
}
.legend-row.dim {
  opacity: 0.4;
}
.legend-row.hidden {
  opacity: 0.5;
}
.legend-row.hidden .legend-name {
  text-decoration: line-through;
}
.legend-swatch-btn {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}
.legend-swatch {
  display: block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
}
.legend-color-input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}
.legend-name-btn {
  flex: 1;
  min-width: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--lg-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.legend-name {
  display: block;
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.legend-stats {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0;
  flex-shrink: 0;
}
.legend-current {
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
  color: var(--lg-text);
}
.legend-range {
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
}
.legend-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: var(--lg-control-sm);
  height: var(--lg-control-sm);
  border: none;
  border-radius: var(--lg-radius-sm);
  background: transparent;
  color: var(--lg-text-faint);
  cursor: pointer;
}
.legend-icon-btn:hover {
  color: var(--lg-text);
  background: var(--lg-bg-hover);
}
.legend-more {
  padding: var(--lg-space-1) var(--lg-space-2);
}
.legend-more-btn {
  width: 100%;
  padding: var(--lg-space-1);
  border: 1px dashed var(--lg-border-strong);
  border-radius: var(--lg-radius-sm);
  background: transparent;
  color: var(--lg-text-dim);
  font: inherit;
  font-size: var(--lg-fs-xs);
  cursor: pointer;
}
.legend-more-btn:hover {
  color: var(--lg-text);
  border-color: var(--lg-accent);
}
.alias-body {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-2);
}
.alias-orig {
  margin: 0;
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
